/**
 * Helpers for displaying booking dates (single date vs stay/range).
 * All formatting uses Europe/London timezone so server-side (UTC) and
 * client-side (any browser TZ) output identical wall-clock strings.
 */

import { BUSINESS_TIMEZONE } from "./timezone";

const TZ = { timeZone: BUSINESS_TIMEZONE } as const;

/** True if the booking spans more than one calendar day (e.g. property stay). */
export function isBookingDateRange(startAt: string, endAt: string): boolean {
  const opts: Intl.DateTimeFormatOptions = {
    ...TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };
  const startDay = new Date(startAt).toLocaleDateString("en-GB", opts);
  const endDay = new Date(endAt).toLocaleDateString("en-GB", opts);
  return startDay !== endDay;
}

const enGBShortDate: Intl.DateTimeFormatOptions = {
  ...TZ,
  weekday: "short",
  month: "short",
  day: "numeric",
};

const enGBTime: Intl.DateTimeFormatOptions = {
  ...TZ,
  hour: "2-digit",
  minute: "2-digit",
};

/** Format booking date for list (short). Single day: "Mon, 12 Jan 2025" + time range. Range: "12 Jan to 17 Jan 2025". */
export function formatBookingDateShort(
  startAt: string,
  endAt: string,
): { dateLine: string; timeLine?: string } {
  const start = new Date(startAt);
  const end = new Date(endAt);
  if (isBookingDateRange(startAt, endAt)) {
    const startStr = start.toLocaleDateString("en-GB", {
      ...TZ,
      day: "numeric",
      month: "short",
    });
    const endStr = end.toLocaleDateString("en-GB", {
      ...TZ,
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    return { dateLine: `${startStr} to ${endStr}` };
  }
  return {
    dateLine: start.toLocaleDateString("en-GB", {
      ...enGBShortDate,
      year: "numeric",
    }),
    timeLine: `${start.toLocaleTimeString("en-GB", enGBTime)} to ${end.toLocaleTimeString("en-GB", enGBTime)}`,
  };
}
